import { useState, useRef, useEffect } from 'react';
import { Flex, Box, Input } from '@chakra-ui/react';
import { HexColorPicker } from 'react-colorful';

interface ColorPickerInputProps {
  value: string;
  onChange: (color: string) => void;
  size?: number;
}

const ColorPickerInput = ({
  value,
  onChange,
  size = 40,
}: ColorPickerInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const normalizedColor = value.startsWith('#') ? value : value ? `#${value}` : '#000000';

  return (
    <Box ref={containerRef} position="relative">
      <Box
        width={`${size}px`}
        height={`${size}px`}
        borderRadius="8px"
        backgroundColor={value || '#000000'}
        borderWidth="1px"
        borderColor="neutral.600"
        cursor="pointer"
        flexShrink={0}
        onClick={() => setIsOpen(prev => !prev)}
        _hover={{ borderColor: 'neutral.500' }}
      />

      {isOpen && (
        <Box
          position="absolute"
          top={`${size + 8}px`}
          left="0"
          zIndex={10}
          backgroundColor="neutral.800"
          borderRadius="12px"
          borderWidth="1px"
          borderColor="neutral.700"
          p="12px"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.4)"
        >
          <HexColorPicker color={normalizedColor} onChange={onChange} />
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#000000"
            mt="10px"
            p="8px"
            fontWeight="400"
            fontSize="13px"
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.700"
            borderRadius="6px"
            _focus={{ borderColor: 'neutral.600', outline: 'none' }}
          />
          <Flex gap="6px" mt="10px" wrap="wrap" maxWidth="200px">
            {[
              '#1976D2', '#2196F3', '#03A9F4', '#00BCD4',
              '#009688', '#4CAF50', '#8BC34A', '#CDDC39',
              '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
              '#F44336', '#E91E63', '#9C27B0', '#673AB7',
              '#3F51B5', '#000000', '#424242', '#FFFFFF',
            ].map((presetColor) => (
              <Box
                key={presetColor}
                width="24px"
                height="24px"
                borderRadius="4px"
                backgroundColor={presetColor}
                borderWidth="1px"
                borderColor={presetColor === '#FFFFFF' ? 'neutral.600' : 'transparent'}
                cursor="pointer"
                onClick={() => onChange(presetColor)}
                _hover={{ transform: 'scale(1.1)' }}
                transition="transform 0.1s"
              />
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default ColorPickerInput;
